import type { SyntheticEvent } from 'react';
import { useState } from 'react';
import styled from '@emotion/styled';
import { Button, tokens } from '@hotjar/hotsauce';

import {
  GenericModal,
  GenericModalFooter,
  GenericModalContent,
} from '@hotjar/webapp/components/GenericModal';
import { VerificationCodeField } from '@hotjar/webapp/components/VerificationCodeField';
import { useTrack } from '@hotjar/tracking';

const Title = styled.h1`
  color: ${tokens.color.text_default};
  font-size: 28px;
  line-height: 38px;
`;

const Paragraph = styled.p`
  margin: ${tokens.spacer.x3} 0 ${tokens.spacer.x4};
  color: ${tokens.color.text_light};
`;

const Email = styled.b`
  font-weight: bold;
  color: ${tokens.color.text_default};
`;

const ProviderLinks = styled.div`
  margin-top: ${tokens.spacer.x4};
  display: flex;
`;

const Link = styled.a`
  margin-right: ${tokens.spacer.x1};
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 12px;
  color: ${tokens.color.text_light};

  &:hover {
    color: ${tokens.color.text_default};
    text-decoration: none;
  }
`;

const ProviderIcon = styled.img`
  margin-right: ${tokens.spacer.x0};
`;

const Error = styled.div`
  margin-top: ${tokens.spacer.x1};
  color: ${tokens.color.text_danger};
`;

const RetryLink = styled.a`
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

interface VerificationCodeModalProps {
  email: string;
  error?: string | null;
  isOpen?: boolean;
  onCancel: () => void;
  onSubmit: (code: string) => void;
  onInput: (code: string) => void;
}

export const VerificationCodeModal = ({
  email,
  error = '',
  isOpen = true,
  onSubmit,
  onCancel,
  onInput,
}: VerificationCodeModalProps): JSX.Element => {
  const [code, setCode] = useState('');
  const { click } = useTrack();

  const handleVerificationCodeChange = (newCode: string) => {
    setCode(newCode);
  };

  const handleVerificationFormSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    onSubmit(code);
  };

  const handleProviderClickTracking = (provider: string) => {
    click(provider, 'Change Email Form', {});
  };

  return (
    <GenericModal
      onSubmit={null}
      onRequestClose={onCancel}
      isOpen={isOpen}
      width={880}
      shouldCloseOnOverlayClick={false}
    >
      <form onSubmit={handleVerificationFormSubmit}>
        <GenericModalContent>
          <Title>Check your email for a code</Title>
          <Paragraph>
            {'We sent a verification code to '}
            <Email>{email}</Email>.
          </Paragraph>
          <VerificationCodeField
            hasError={!!error}
            onChange={handleVerificationCodeChange}
            onInput={(newCode: number[]) => onInput(newCode.join(''))}
          />
          {error && <Error>{error}</Error>}
          <ProviderLinks>
            <Link
              href="https://mail.google.com/"
              onClick={() => handleProviderClickTracking('gmail')}
              data-testid="gmail-link"
              target="_blank"
              rel="noreferrer"
            >
              <ProviderIcon
                // FIXME: global-require
                // eslint-disable-next-line global-require
                src={require('@hotjar/shared-assets/images/signup/gmail.png')}
                width="16"
              />
              Open Gmail
            </Link>
            <Link
              href="https://login.live.com/login.srf"
              onClick={() => handleProviderClickTracking('outlook')}
              data-testid="outlook-link"
              target="_blank"
              rel="noreferrer"
            >
              <ProviderIcon
                // FIXME: global-require
                // eslint-disable-next-line global-require
                src={require('@hotjar/shared-assets/images/signup/outlook.png')}
                width="16"
              />
              Open Outlook
            </Link>
          </ProviderLinks>
          <Paragraph>
            Can&apos;t find the email? Check your spam folder, or{' '}
            <RetryLink onClick={onCancel} data-testid="retry">
              re-enter your email and try again
            </RetryLink>
            .
          </Paragraph>
        </GenericModalContent>
        <GenericModalFooter>
          <Button kind="ghost" onClick={onCancel} data-testid="cancel">
            Cancel
          </Button>
          <Button type="submit" data-testid="submit">
            Confirm Code
          </Button>
        </GenericModalFooter>
      </form>
    </GenericModal>
  );
};
